import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TbEye, TbFileText, TbTrash, TbDeviceDesktop, TbUpload, TbLock } from "react-icons/tb";
import { useScanResults } from "../../hooks/useScanResults";
import { useSort } from "../../hooks/useSort";
import { fmtDate } from "../../lib/dates";
import { Button, Alert, EmptyState, SkeletonRows, SortHeader, ConfirmModal, Segmented } from "../../components";
import UploadScanModal from "./UploadScanModal";
import ProvisionLocalhostModal from "./ProvisionLocalhostModal";
import { usePlan } from "../../lib/plan";
import { UpgradeModal } from "../../shell/PlanGate";
import "./ScanResults.css";

// Scan results for one organization. A v2 screen, not the old PastScans restyled:
// nothing here imports from src/components.
//
// The two sources are genuinely different tables with different columns and different
// delete routes, so they are two column sets rather than one merged table.

const INTERNAL_COLS = [
  { key: "index",      label: "#",           width: 40 },
  { key: "file_path",  label: "File" },
  { key: "username",   label: "Uploaded by" },
  { key: "created_at", label: "Created" },
];

const EXTERNAL_COLS = [
  { key: "index",      label: "#",      width: 40 },
  { key: "domain",     label: "Domain" },
  { key: "score",      label: "Score" },
  { key: "username",   label: "Name" },
  { key: "email",      label: "Email" },
  { key: "created_at", label: "Created" },
];

const fileName = (p) => (p || "").split("/").pop();

const ScanResults = ({ organizationId, organizationName }) => {
  const navigate = useNavigate();
  const [source, setSource] = useState("internal");
  const [confirm, setConfirm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [upselling, setUpselling] = useState(false);
  // Snapshot is a plan feature (Global Admin → Scan Modules → Snapshot Scanner). Locked,
  // the button stays — with a lock — and explains the upgrade instead of opening.
  const snapshotLocked = !usePlan().can("SNAPSHOT");

  const { results, loading, error, busyId, reload, remove, view } = useScanResults(organizationId, source);
  const cols = source === "internal" ? INTERNAL_COLS : EXTERNAL_COLS;
  const { sort, toggleSort, sorted } = useSort(results, source === "internal" ? "created_at" : "created_at", ["created_at"]);
  const rowKey = (r) => (source === "internal" ? r.scan_id : r.id);

  // Report generation goes straight to picking a template. It used to hand off to the
  // v1 two-step wizard, whose first step asked which scan you wanted — immediately after
  // you clicked the button on the scan you wanted. The scan is already decided here, so
  // the only question left is the template.
  //
  // Which side the path lands on matters: the builder reads internalScanPath and
  // externalScanPath separately to know what data it has.
  const generateReport = (r) =>
    navigate("/report-builder/pick-template", {
      state: {
        internalScanPath: source === "internal" ? r.file_path : null,
        externalScanPath: source === "external" ? r.file_path : null,
        organizationId,
        scanLabel: source === "internal" ? fileName(r.file_path) : (r.domain || "Scan"),
        scanDate: fmtDate(r.created_at),
      },
    });

  return (
    <div className="v2-scans">
      <div className="v2-scans-row">
        <div>
          <div className="v2-scans-label">Scan source</div>
          <div className="v2-scans-note">Choose where scan results come from</div>
        </div>
        <Segmented
          ariaLabel="Scan source"
          value={source}
          onChange={setSource}
          options={[{ value: "internal", label: "Internal" }, { value: "external", label: "External" }]}
        />
      </div>

      <Alert>{error}</Alert>

      <div className="v2-tablewrap">
        <table className="v2-table">
          <thead>
            <tr>
              {cols.map((c) =>
                c.key === "index" ? (
                  <th key={c.key} style={{ width: c.width }}>#</th>
                ) : (
                  <SortHeader key={c.key} label={c.label} colKey={c.key} sort={sort} onSort={toggleSort} />
                )
              )}
              <th className="v2-num">View</th>
              <th className="v2-num">Report</th>
              <th className="v2-num">Delete</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows rows={3} cols={cols.length + 3} />
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={cols.length + 3}>
                  <EmptyState title="No scan results">
                    {source === "internal"
                      ? "Nothing has been uploaded for this organization yet."
                      : "No external scans have been run against this organization yet."}
                  </EmptyState>
                </td>
              </tr>
            ) : (
              sorted.map((r, i) => {
                const id = rowKey(r);
                const busy = busyId === id;
                return (
                  // The scan-* classes and data-label drive the phone layout in
                  // ScanResults.css, where each row becomes a card: the title cell, a
                  // labelled line per field, and the three actions along the bottom.
                  <tr key={id} className="v2-scanrow">
                    <td className="v2-num v2-dim scan-idx">{i + 1}</td>
                    {source === "internal" ? (
                      <>
                        <td className="v2-mono scan-title" title={fileName(r.file_path)}>{fileName(r.file_path)}</td>
                        <td className="v2-dim scan-field" data-label="Uploaded by">{r.username || "—"}</td>
                      </>
                    ) : (
                      <>
                        <td className="v2-mono scan-title">{r.domain || "—"}</td>
                        <td className="v2-num scan-field" data-label="Score">{r.score ?? "—"}</td>
                        <td className="v2-dim scan-field" data-label="Name">{r.username || "—"}</td>
                        <td className="v2-dim v2-mono scan-field" data-label="Email">{r.email || "—"}</td>
                      </>
                    )}
                    <td className="v2-dim scan-field" data-label="Created">{fmtDate(r.created_at)}</td>
                    <td className="v2-num scan-act scan-act-first">
                      <Button icon title="View raw scan" disabled={busy} onClick={() => view(r.file_path, id)}>
                        <TbEye size={15} />
                      </Button>
                    </td>
                    <td className="v2-num scan-act">
                      <Button icon title="Generate report" onClick={() => generateReport(r)}>
                        <TbFileText size={15} />
                      </Button>
                    </td>
                    <td className="v2-num scan-act">
                      {/* Delete keeps the only colour in the row: it is the one action
                          here that cannot be undone. */}
                      <Button icon variant="danger" title="Delete" disabled={busy} onClick={() => setConfirm(r)}>
                        <TbTrash size={15} />
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Not in the design kit — the kit was drawn from the scan-result columns and
          missed it. It is the only way to start a Pipsqueak Snapshot from here, so it
          stays. */}
      {/* The two ways scan data gets here: run a scan, or bring one you already have.
          Upload used to be a sidebar screen whose first question was which organization
          the file belonged to — which is the one thing this context already knows. */}
      {source === "internal" && (
        <div className="v2-provision">
          <span className="v2-provision-icon"><TbDeviceDesktop size={20} /></span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="v2-provision-title">Add scan data</div>
            <div className="v2-provision-note">
              Run a Pipsqueak Snapshot against this organization's environment, or upload
              a scan the Pipsqueak scanner has already produced.
            </div>
          </div>
          <div className="v2-provision-actions">
            <Button onClick={() => setUploading(true)}>
              <TbUpload size={15} /> Upload scan data
            </Button>
            {/* Was navigate("/scan-links/new"), which is the EXTERNAL domain-scan link
                generator — a different flow entirely, and one that dropped v2 users onto
                a v1 page. The localhost flow is a dialog here, matching what v1 shows
                inline once you click through. */}
            <Button variant="primary"
                    onClick={() => (snapshotLocked ? setUpselling(true) : setProvisioning(true))}>
              {snapshotLocked && <TbLock size={14} />} Pipsqueak Snapshot
            </Button>
          </div>
        </div>
      )}

      {provisioning && (
        <ProvisionLocalhostModal
          organizationId={organizationId}
          organizationName={organizationName}
          onClose={() => setProvisioning(false)}
        />
      )}

      {upselling && <UpgradeModal gate="SNAPSHOT" onClose={() => setUpselling(false)} />}

      {uploading && (
        <UploadScanModal
          organizationId={organizationId}
          organizationName={organizationName}
          onClose={() => setUploading(false)}
          onUploaded={reload}
        />
      )}

      {confirm && (
        <ConfirmModal
          title="Delete this scan?"
          body={`${source === "internal" ? fileName(confirm.file_path) : confirm.domain} will be removed permanently. This cannot be undone.`}
          busy={busyId === rowKey(confirm)}
          onClose={() => setConfirm(null)}
          onConfirm={async () => { await remove(rowKey(confirm)); setConfirm(null); }}
        />
      )}
    </div>
  );
};

export default ScanResults;
